import { useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import {
  BADGE_DEFINITION_KIND,
  BADGE_PROFILE_KIND,
  BADGE_PROFILE_KIND_LEGACY,
  parseBadgeDefinition,
  parseProfileBadges,
  type BadgeData,
  type BadgeRef,
} from '@/lib/badges';

/**
 * The badges a profile has accepted, in the order they arranged them.
 *
 * Two round-trips: the owner's profile badges event (author-filtered — the
 * showcase is only meaningful as a claim by its owner), then the referenced
 * kind 30009 definitions, grouped by issuer so one issuer costs one filter.
 */
export function useProfileBadges(pubkey: string | undefined): {
  badges: BadgeData[];
  isLoading: boolean;
} {
  const { nostr } = useNostr();

  const listQuery = useQuery<BadgeRef[]>({
    queryKey: ['profile-badges', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);
      const events = await nostr.query(
        [
          { kinds: [BADGE_PROFILE_KIND], authors: [pubkey], limit: 1 },
          {
            kinds: [BADGE_PROFILE_KIND_LEGACY],
            authors: [pubkey],
            '#d': ['profile_badges'],
            limit: 1,
          },
        ],
        { signal },
      );
      if (!events.length) return [];
      // Both kinds can exist during the 30008 -> 10008 migration; newest wins.
      const newest = events.reduce((a, b) => (b.created_at > a.created_at ? b : a));
      return parseProfileBadges(newest);
    },
    enabled: !!pubkey,
    staleTime: 5 * 60 * 1000,
  });

  const refs = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const key = refs.map((r) => r.aTag).join(',');

  const defsQuery = useQuery({
    queryKey: ['badge-definitions', key],
    queryFn: async (c) => {
      if (!refs.length) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      const byAuthor = new Map<string, Set<string>>();
      for (const ref of refs) {
        const identifiers = byAuthor.get(ref.pubkey) ?? new Set<string>();
        identifiers.add(ref.identifier);
        byAuthor.set(ref.pubkey, identifiers);
      }

      const filters = Array.from(byAuthor, ([author, identifiers]) => ({
        kinds: [BADGE_DEFINITION_KIND],
        authors: [author],
        '#d': Array.from(identifiers),
        limit: identifiers.size,
      }));

      return nostr.query(filters, { signal });
    },
    enabled: refs.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  const badges = useMemo(() => {
    const byATag = new Map<string, BadgeData>();
    for (const event of defsQuery.data ?? []) {
      const parsed = parseBadgeDefinition(event);
      if (parsed) byATag.set(parsed.aTag, parsed);
    }
    // Preserve the owner's ordering; drop references whose definition is
    // missing rather than printing a blank tile.
    return refs
      .map((ref) => byATag.get(ref.aTag))
      .filter((badge): badge is BadgeData => !!badge);
  }, [refs, defsQuery.data]);

  return {
    badges,
    isLoading: listQuery.isLoading || (refs.length > 0 && defsQuery.isLoading),
  };
}
